/**
 * indoor_combat_client.ts — INDOOR COMBAT, the client's half (the interiors programme, slice K, 2026-10-02).
 *
 * THE CLIENT MIRRORS THE JUDGE. The server decides every blow (`server/src/lib/indoor_combat_ops.ts sightBetween`);
 * this file asks the SAME pure rule (`./indoor_combat.js`, byte-identical on the server) with the rows the client
 * holds, for three readers:
 *   · the reticle's soft target and the red "blocked" mark          (`clientSight`,  WildsModule)
 *   · who is offered as a mark at all                               (`remoteSeat().hidden`, WildsModule)
 *   · where another traveler's body is DRAWN — on THEIR floor — and whether it is drawn at all from where I stand
 *                                                                   (`remoteSeat`, AtlasAvatarModule)
 *
 * WHAT THE CLIENT KNOWS.
 *   me            my own `player_interior` row (row-level secured to me): building, floor, room, `verified`
 *   others        the PUBLIC storey mirror `interior_occupant`: building + the floor the JUDGE seats them on (an
 *                 unverified row is published as the ground floor — where the rule puts a body it cannot prove a floor
 *                 for). Never the room.
 *   the building  its frame from the `structure` row (the server's own frame), its core and storeys from the footprint
 *                 table; the ROOM (partitions, furniture, cover, the lift shaft) only for the interior I stand in — the
 *                 active descriptor — and only when it is the room the server judges by (the pin's digest, or my own
 *                 row verified). From outside, the client predicts the shell and the door, not the room behind it.
 *
 * THE MIRROR'S UPKEEP. The public row is a copy the SERVER makes of its own judged row; the client only asks for it
 * (`syncInteriorOccupant`, no arguments) when its own presence row changes. A client that never asks is caught up by
 * the server's sweep within 30 s.
 *
 * Everything here is a PREDICTION: the result row is the authority (a blocked blow comes back `blocked`).
 */
import { atlas } from '../state.js';
import { enterables, interiorEligibleDeed } from './building_interiors.js';
import { activeInterior, floorYOf } from './interior_wiring.js';
import { ROOMS_FLAG, deedPlanBox, planHasRooms, roomPlanFrom, type RoomPlan } from './interior_rooms.js';
import { tableStamp } from './fast_find.js';
import {
  INDOOR_COMBAT_FLAG, blockWords, coreHalf, indoorRelation, partyAt, storeysOf,
  type IcBlock, type IcBuilding, type IcParty,
} from './indoor_combat.js';

interface IdLike { toHexString(): string }
interface InteriorRow { identity: IdLike; buildingId: string; floor: number; roomId: string; verified: boolean }
interface OccupantRow { identity: IdLike; buildingId: string; floor: number }
interface StructureRow { structureId: bigint; structureType: string; posX: number; posZ: number; yaw: number }
interface FlagRow { key: string; enabled: boolean }
interface TableLike<T> { iter(): Iterable<T>; onInsert?(cb: (...a: unknown[]) => void): void; onUpdate?(cb: (...a: unknown[]) => void): void; onDelete?(cb: (...a: unknown[]) => void): void }
interface Db {
  playerInterior?: TableLike<InteriorRow>;
  interiorOccupant?: TableLike<OccupantRow>;
  structure?: TableLike<StructureRow> & { structureId?: { find(id: bigint): StructureRow | undefined } };
  platformFlag?: { key?: { find(k: string): FlagRow | undefined } };
}

export type ClientSight =
  | { kind: 'street' }
  | { kind: 'clear' }
  | { kind: 'blocked'; why: IcBlock; words: string }
  | { kind: 'cover'; height: number; at: { x: number; z: number } };

export interface RemoteSeat {
  /** The height the body's feet belong at (absolute), or null = wherever the ground rule puts it. */
  y: number | null;
  /** Not drawn (and not offered as a mark): the building's shell, or a floor, stands between us. */
  hidden: boolean;
  /** The storey the judge seats them on, or -1 = not indoors. */
  floor: number;
}

interface State {
  conn: unknown;
  hooked: boolean;
  occ: Map<string, { building: string; floor: number }>;
  occStamp: number; occAt: number;
  plan: { id: string; version: number; plan: RoomPlan | null } | null;
  syncTimer: ReturnType<typeof setTimeout> | null;
  stats: { syncs: number; syncErrors: number; sights: number; blocked: number; cover: number; hidden: number; lifted: number; lastSight: string };
}
// globalThis-backed (the instance-split law: the dev graph can resolve one specifier to two module instances)
const G = globalThis as unknown as { __atlasIndoorCombat?: State };
const S: State = (G.__atlasIndoorCombat ??= {
  conn: null, hooked: false, occ: new Map(), occStamp: Number.NaN, occAt: -1e9, plan: null, syncTimer: null,
  stats: { syncs: 0, syncErrors: 0, sights: 0, blocked: 0, cover: 0, hidden: 0, lifted: 0, lastSight: '' },
});

const db = (): Db | null => (atlas.conn ? (atlas.conn.db as unknown as Db) : null);
const NO_SEAT: RemoteSeat = { y: null, hidden: false, floor: -1 };

/** Is the indoor rule on? (the server's two levers, mirrored: ABSENT = ON for both). */
export function indoorCombatClientOn(): boolean {
  try {
    const f = db()?.platformFlag?.key;
    if (!f) return true;
    const a = f.find(INDOOR_COMBAT_FLAG), b = f.find(ROOMS_FLAG);
    return (a ? a.enabled : true) && (b ? b.enabled : true);
  } catch { return true; }
}

/** Subscriptions + the own-row watch. Idempotent per connection; call it from anything that reads this file. */
export function ensureIndoorClient(): void {
  const conn = atlas.conn;
  if (!conn || S.conn === conn) return;
  S.conn = conn; S.hooked = false; S.occ.clear(); S.occStamp = Number.NaN; S.occAt = -1e9; S.plan = null;
  for (const q of ['SELECT * FROM player_interior', 'SELECT * FROM interior_occupant']) {
    try { conn.subscriptionBuilder().subscribe([q]); } catch { /* a server older than the slice */ }
  }
  try {
    // (row-level security hands this client its OWN presence row only: any event on the table is mine)
    const t = (conn.db as unknown as Db).playerInterior;
    const ask = (): void => requestOccupantSync();
    t?.onInsert?.(ask); t?.onUpdate?.(ask); t?.onDelete?.(ask);
    S.hooked = !!t?.onInsert;
  } catch { /* a server older than the slice */ }
}

/** Ask the server to copy my judged presence row into the public storey mirror (debounced; never an argument). */
export function requestOccupantSync(): void {
  if (S.syncTimer !== null) return;
  S.syncTimer = setTimeout(() => {
    S.syncTimer = null;
    const r = (atlas.conn?.reducers as unknown as { syncInteriorOccupant?: (a: Record<string, never>) => Promise<void> } | undefined)?.syncInteriorOccupant;
    if (!r) return;
    S.stats.syncs++;
    r.call(atlas.conn!.reducers, {}).catch(() => { S.stats.syncErrors++; });
  }, 120);
}

function myRow(): InteriorRow | null {
  try {
    const t = db()?.playerInterior;
    if (!t) return null;
    for (const r of t.iter()) return r; // (row-level secured: mine is the only row here)
    return null;
  } catch { return null; }
}

/** Me, as the relation sees me: my predicted 2D point and — when my row names the interior I stand in — its floor and room. */
export function myParty(): IcParty {
  const base = partyAt(atlas.localX, atlas.localZ);
  if (!indoorCombatClientOn()) return base;
  const row = myRow();
  if (!row || !row.buildingId.startsWith('deed:') || atlas.interiorId !== row.buildingId) return base;
  return { x: atlas.localX, z: atlas.localZ, building: row.buildingId, floor: row.floor, room: row.roomId, verified: row.verified };
}

function occupants(): Map<string, { building: string; floor: number }> {
  const t = db()?.interiorOccupant;
  if (!t) { if (S.occ.size) S.occ.clear(); return S.occ; }
  const stamp = tableStamp(t), now = performance.now();
  // (an unpatched table has no stamp: refresh four times a second instead)
  if (Number.isNaN(stamp) ? now - S.occAt < 250 : stamp === S.occStamp) return S.occ;
  S.occStamp = stamp; S.occAt = now;
  S.occ.clear();
  try { for (const o of t.iter()) S.occ.set(o.identity.toHexString(), { building: o.buildingId, floor: o.floor }); } catch { /* mid-teardown */ }
  return S.occ;
}

/** Another traveler as the relation sees them: where their body is and what the public mirror says of their storey. */
export function partyOfRemote(hex: string, x: number, z: number): IcParty {
  const o = indoorCombatClientOn() ? occupants().get(hex) : undefined;
  if (!o) return partyAt(x, z);
  // (the mirror carries the storey the judge uses — an unverified row is published as the ground floor)
  return { x, z, building: o.building, floor: o.floor, room: '', verified: true };
}

function frameOf(id: string): { x: number; z: number; yaw: number; type: string } | null {
  const m = /^deed:(\d{1,20})$/.exec(id);
  if (!m) return null;
  try {
    const s = db()?.structure?.structureId?.find(BigInt(m[1]));
    if (s) return { x: s.posX, z: s.posZ, yaw: s.yaw, type: s.structureType }; // the server's own frame
  } catch { /* no row in my window */ }
  const e = enterables.get(id);
  return e ? { x: e.x, z: e.z, yaw: e.yaw, type: e.key.replace(/^deed:/, '') } : null;
}

/** The room plan of the interior I stand in, as the SERVER builds it (the footprint table's box, the pack seated). */
function activePlan(id: string, type: string): { plan: RoomPlan; pinned: boolean } | null {
  const a = activeInterior();
  if (!a || a.id !== id || a.route !== 'descriptor' || !a.descriptor) return null;
  if (!S.plan || S.plan.id !== id || S.plan.version !== a.version) {
    let plan: RoomPlan | null = null;
    try { plan = roomPlanFrom(a.descriptor, { ...deedPlanBox(type), key: `deed:${type}` }, type === 'guild_hall' ? 'all' : (a.pack ?? 0)); } catch { plan = null; }
    S.plan = { id, version: a.version, plan };
  }
  if (!S.plan.plan) return null;
  return { plan: S.plan.plan, pinned: !!a.pin && a.pin.digest !== '' && a.pin.digest === a.descriptor.digest };
}

/** The building a presence id names, as the pure rule wants it (null = not a deed anyone walks into, or not in my window). */
export function clientBuilding(id: string): IcBuilding | null {
  const f = frameOf(id);
  if (!f) return null;
  const box = deedPlanBox(f.type);
  if (!interiorEligibleDeed(f.type, box.w, box.h, box.d)) return null;
  const b: IcBuilding = { id, x: f.x, z: f.z, yaw: f.yaw, hw: coreHalf(box.w), hd: coreHalf(box.d), floors: storeysOf(box.h), geom: null, shaft: null };
  const p = activePlan(id, f.type);
  if (p) {
    b.hw = p.plan.hw; b.hd = p.plan.hd; b.floors = p.plan.floors.length;
    // the room is the judge's only when its digest is the pin's, or the server verified MY row against it
    const mine = myRow();
    if (p.pinned || (planHasRooms(p.plan) && !!mine && mine.buildingId === id && mine.verified)) { b.geom = p.plan.floors; b.shaft = p.plan.shaft ?? null; }
  }
  return b;
}

function relate(a: IcParty, t: IcParty, ranged: boolean): ClientSight {
  if (a.building === '' && t.building === '') return { kind: 'street' };
  const id = a.building !== '' ? a.building : t.building;
  const b = a.building !== '' && t.building !== '' && a.building !== t.building ? null : clientBuilding(id);
  const r = indoorRelation(a, t, b, { ranged });
  if (r.kind === 'blocked') return { kind: 'blocked', why: r.why, words: blockWords(r.why) };
  if (r.kind === 'cover') return { kind: 'cover', height: r.height, at: r.at };
  return r.indoors ? { kind: 'clear' } : { kind: 'street' };
}

/**
 * What the indoor rule says of a blow from me to a mark. `street` = it has no opinion (nobody is indoors by a row):
 * the caller keeps its own surface query, exactly as before this slice.
 */
export function clientSight(target: { kind: string; id: string; x: number; z: number }, ranged: boolean): ClientSight {
  if (!indoorCombatClientOn()) return { kind: 'street' };
  ensureIndoorClient();
  const t = target.kind === 'player' ? partyOfRemote(target.id, target.x, target.z) : partyAt(target.x, target.z);
  const v = relate(myParty(), t, ranged);
  S.stats.sights++;
  if (v.kind === 'blocked') S.stats.blocked++; else if (v.kind === 'cover') S.stats.cover++;
  S.stats.lastSight = v.kind === 'blocked' ? `blocked(${v.why})` : v.kind === 'cover' ? `cover(${v.height.toFixed(2)})` : v.kind;
  return v;
}

/**
 * Where another traveler's body belongs, and whether it is drawn from where I stand.
 *   · no public row                      → as before (the ground rule seats it; always drawn)
 *   · in the building I am in            → drawn on THEIR floor's slab (the slabs between us hide what they hide)
 *   · in a building I am not in          → the exterior stands between us: hidden, unless they stand on the ground
 *                                          floor and the door's own line joins us (the one way a blow could pass)
 */
export function remoteSeat(hex: string, x: number, z: number): RemoteSeat {
  if (!indoorCombatClientOn()) return NO_SEAT;
  ensureIndoorClient();
  const o = occupants().get(hex);
  if (!o) return NO_SEAT;
  const e = enterables.get(o.building);
  const y = e ? e.groundY + floorYOf(o.floor) : null;
  if (y !== null) S.stats.lifted++;
  if (atlas.interiorId === o.building) return { y, hidden: false, floor: o.floor };
  let hidden = o.floor !== 0;
  if (!hidden) hidden = relate(myParty(), { x, z, building: o.building, floor: 0, room: '', verified: true }, true).kind === 'blocked';
  if (hidden) S.stats.hidden++;
  return { y, hidden, floor: o.floor };
}

/** The probe's lever (`window.__indoorCombat()`): what the client holds and what it last predicted. */
export function indoorCombatDiag(): Record<string, unknown> {
  ensureIndoorClient();
  const me = myParty(), row = myRow();
  const b = me.building ? clientBuilding(me.building) : null;
  return {
    on: indoorCombatClientOn(), hooked: S.hooked,
    me: { building: me.building, floor: me.floor, room: me.room, verified: me.verified, row: !!row, interiorId: atlas.interiorId },
    building: b ? { id: b.id, hw: b.hw, hd: b.hd, floors: b.floors, geom: !!b.geom, walls: b.geom ? b.geom.map((f) => f?.walls.length ?? 0) : null, shaft: !!b.shaft } : null,
    occupants: [...occupants()].map(([hex, o]) => ({ hex: hex.slice(0, 12), ...o })),
    stats: { ...S.stats },
  };
}
